import React, {useState, useMemo, ReactElement, useEffect, useRef} from 'react';
import RightPanelHeader from './component/RightPanelHeader';
import {Box, Typography} from '@mui/material';
import ReportsDataGrid, {ReportGridProps} from './ReportDataGrid';
import {ColumnConfig, RightPanelConfig} from '../../config/reportTypes';
import {ReactComponent as NoDataIcon} from '../../../../../assets/images/no-data-found.svg';
import {t} from 'i18next';
import _ from 'lodash';
import './RightPanel.scss';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ExportDataMain from '../../../data-export-report/ExportDataMain';
import {ActionMenuPayloadType} from '../../reports/search-term/SearchTermReportContainer';
import {CustomMenuItem} from '../../../../common/arr-menu/ArrMenu';

interface RightPanelProps {
  isNoData: boolean;
  isSaveReportInActive: boolean;
  isSavePanelOpen: boolean;
  setIsSavePanelOpen: (value: boolean) => void;
  loading?: boolean;
  data: any;
  columns: ColumnConfig[];
  mapRows?: (data: any) => any[];
  rightPanelHeaderConfig?: RightPanelConfig;
  canSave?: boolean;
  getRowId?: (row: any) => string | number;
  onColumnToggleAction?: (columnId: string) => void; // Added new prop
  onTileChange?: (key: string, selected: boolean) => void;
  totalSelectedCount?: number; // Added new prop
  customHeaderContent?: ReactElement;
  gridConfig?: ReportGridProps;
  isQueryRun?: boolean;
  showExportDataReportContent?: boolean;
  filters?: any;
  onRunStatusChange?: (ran: boolean, savePayload?: any) => void;
  isReportShared?: boolean;
  rightContentComponent?: ReactElement;
  savedData?: any;
  actionMenuPayload?: ActionMenuPayloadType;
  onActionSelect?: (
    action: CustomMenuItem,
    callBackForDoc?: (docId: string, source: string) => void
  ) => void;
  resetSignal?: boolean;
}

const RightPanel: React.FC<RightPanelProps> = ({
  isSavePanelOpen,
  setIsSavePanelOpen,
  loading = false,
  data,
  columns,
  mapRows,
  rightPanelHeaderConfig,
  isSaveReportInActive = false,
  canSave = false,
  getRowId,
  onColumnToggleAction,
  onTileChange,
  totalSelectedCount,
  customHeaderContent,
  gridConfig,
  isQueryRun,
  showExportDataReportContent = false,
  filters = null,
  onRunStatusChange,
  isReportShared,
  rightContentComponent,
  savedData,
  actionMenuPayload,
  onActionSelect,
  resetSignal = false,
}) => {
  const [allColumns, setAllColumns] = useState(columns);
  const isInitialized = useRef(false);
  const userToggledFields = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Keep saved deselected columns hidden when the parent re-creates columns
    const savedHidden: number[] = savedData?.selectedCustomColumns ?? [];
    setAllColumns(
      columns.map((col) =>
        savedHidden.includes(col.index as number) &&
        !userToggledFields.current.has(col.field)
          ? {...col, isVisible: false}
          : col
      )
    );
  }, [columns]);

  useEffect(() => {
    if (isInitialized.current) return;
    let updatedColumns = [...columns];

    if (savedData?.selectedCustomColumns?.length) {
      updatedColumns = updatedColumns.map((col) => {
        const isIndexSelected = savedData.selectedCustomColumns.includes(
          col.index
        );
        return {
          ...col,
          isVisible: isIndexSelected ? false : col.isVisible,
        };
      });
    }

    setAllColumns(updatedColumns);
    isInitialized.current = true;
  }, [columns, savedData]);

  /** Handle column visibility toggle */
  const handleColumnSelectionChange = (item: {id: string | number}) => {
    userToggledFields.current.add(item.id.toString());
    setAllColumns((prev) =>
      prev.map((col) =>
        col.field === item.id.toString()
          ? {...col, isVisible: !col.isVisible}
          : col
      )
    );
    if (onColumnToggleAction) {
      onColumnToggleAction(item.id.toString());
    }
  };

  useEffect(() => {
    isInitialized.current = false;
    userToggledFields.current.clear();
  }, [savedData]);

  /** Prepare table columns for header */
  const tableColumns = useMemo(
    () =>
      allColumns?.map((col) => ({
        id: col.field,
        name: col.headerName,
        checked: col.isVisible,
      })),
    [allColumns]
  );

  /** Filter columns for DataGrid */
  const filteredColumns = useMemo(
    () => allColumns.filter((col) => col.isVisible),
    [allColumns]
  );

  const rows = useMemo(() => (mapRows ? mapRows(data) : []), [data, mapRows]);

  const isShowNoRowOverlay = useMemo(() => {
    if (_.isNil(isQueryRun)) {
      return rows.length === 0;
    }
    return !isQueryRun;
  }, [isQueryRun, rows]);

  const NoRowsOverlay = () => (
    <Box className="no-result-data-found">
      <NoDataIcon data-testid="info-icon" />
      <Typography
        className="typo-text headline3_size no-result-text"
        tabIndex={0}
        data-testid={`right-panel-no-message`}
      >
        {data?.noDataMessage ??
          t('reports.reviewResults.rightPanel.noResultFound')}
      </Typography>
    </Box>
  );

  const renderContent = () => {
    if (rightContentComponent) {
      return rightContentComponent;
    } else {
      return (
        <Box className="doc-datagrid-container">
          <ReportsDataGrid
            loading={loading}
            columns={filteredColumns}
            rows={rows}
            getRowId={getRowId}
            aditionalGridProps={gridConfig}
          />
        </Box>
      );
    }
  };

  return (
    <Box className="standard-report-template-right-panel">
      {/* Table Header */}
      <Box>
        <RightPanelHeader
          rightPanelHeaderConfig={rightPanelHeaderConfig}
          isSavePanelOpen={isSavePanelOpen}
          setIsSavePanelOpen={setIsSavePanelOpen}
          onUploadClick={rightPanelHeaderConfig?.onUploadClick}
          onSaveClick={() => {
            localStorage.setItem('visibleColumns', JSON.stringify(allColumns));
          }}
          tableColumns={tableColumns}
          onTableColumnSelectionChange={handleColumnSelectionChange}
          isNoData={rows.length === 0}
          isSaveReportInActive={isSaveReportInActive}
          canSave={canSave}
          isQueryRun={isQueryRun}
          data={data}
          onTileChange={onTileChange}
          totalSelectedCount={totalSelectedCount}
          isExportDataReport={showExportDataReportContent}
          actionMenuPayload={actionMenuPayload}
          onActionSelect={onActionSelect}
        />
        {customHeaderContent ?? null}
      </Box>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
          }}
        >
          <ArrLoadingSpinner show={loading} overlay={true} isOpen />
        </Box>
      ) : isShowNoRowOverlay && !showExportDataReportContent ? (
        NoRowsOverlay()
      ) : showExportDataReportContent ? (
        <Box className="doc-datagrid-container">
          <ExportDataMain
            isReset={resetSignal}
            data={data}
            filters={filters ?? null}
            onRunStatusChange={onRunStatusChange}
            isReportShared={isReportShared}
          />
        </Box>
      ) : (
        renderContent()
      )}
    </Box>
  );
};

export default RightPanel;
